import {
  Box,
  Button,
  Card,
  Container,
  Flex,
  Heading,
  HStack,
  Stack,
  Text,
  Code,
  VStack,
} from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';
import { ArrowRightIcon, KeyIcon, BookOpenIcon, BeakerIcon } from '@heroicons/react/24/outline';

const codeSample = `curl https://coachhub.athleteera.app/api/public/v1/workspace \\
  -H "Authorization: Bearer ae_live_xxxxx_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"`;

export default function DevelopersLanding() {
  return (
    <Box minH='100vh' bg='bg.subtle' py={{ base: 8, md: 16 }}>
      <Container maxW='4xl'>
        <Stack gap={10}>
          <Stack gap={4} textAlign='center'>
            <Heading size={{ base: '3xl', md: '5xl' }} letterSpacing='tight'>
              Athlete Era Developer Portal
            </Heading>
            <Text color='fg.muted' fontSize={{ base: 'md', md: 'lg' }} maxW='2xl' mx='auto'>
              Read your workspace&apos;s content, collections, and calendar events programmatically.
              A read-only public API designed for integrations and external tooling.
            </Text>
            <HStack justify='center' gap={3} pt={2}>
              <Button colorPalette='brand' asChild size='lg'>
                <NavLink to='/developers/docs'>
                  <BookOpenIcon width={18} height={18} />
                  View API reference
                  <ArrowRightIcon width={16} height={16} />
                </NavLink>
              </Button>
              <Button variant='outline' asChild size='lg'>
                <NavLink to='/auth/login'>
                  <KeyIcon width={18} height={18} />
                  Sign in to create a key
                </NavLink>
              </Button>
            </HStack>
          </Stack>

          <Card.Root>
            <Card.Body>
              <Stack gap={4}>
                <Heading size='lg'>Get started</Heading>
                <VStack align='stretch' gap={3} pl={4} borderLeft='4px solid' borderColor='brand.solid'>
                  <Step
                    number='1'
                    title='Create an API key'
                    body='Sign in to your workspace, open Settings → Developer (API keys), and click "Create API key". Copy the key when shown — it is only displayed once.'
                  />
                  <Step
                    number='2'
                    title='Make your first request'
                    body='Send the key in the Authorization header on every request.'
                  />
                  <Step
                    number='3'
                    title='Read your content'
                    body='Use the documented endpoints to fetch content types, contents, collections, and calendar events scoped to your workspace.'
                  />
                  <Step
                    number='4'
                    title='Test without leaving the browser'
                    body='Every endpoint in the reference has a "Test Request" panel. Paste your key once in the Authentication section and run live calls from the page.'
                  />
                </VStack>
              </Stack>
            </Card.Body>
          </Card.Root>

          <Card.Root>
            <Card.Body>
              <Stack gap={3}>
                <Heading size='md'>Example request</Heading>
                <Box
                  as='pre'
                  bg='gray.900'
                  color='whiteAlpha.900'
                  p={4}
                  rounded='md'
                  fontSize='sm'
                  overflowX='auto'
                  fontFamily='mono'
                >
                  {codeSample}
                </Box>
                <Text color='fg.muted' fontSize='sm'>
                  Replace the bearer token with your real key. The response is wrapped in{' '}
                  <Code>{'{ "data": {...}, "meta": {...} }'}</Code> on success and{' '}
                  <Code>{'{ "error": { "code", "message" } }'}</Code> on failure.
                </Text>
                <HStack pt={2}>
                  <Button variant='outline' size='sm' asChild>
                    <NavLink to='/developers/docs'>
                      <BeakerIcon width={16} height={16} />
                      Try requests in your browser
                    </NavLink>
                  </Button>
                </HStack>
              </Stack>
            </Card.Body>
          </Card.Root>

          <Flex justify='center' pt={4}>
            <Text color='fg.muted' fontSize='sm'>
              Need help? Email{' '}
              <Box as='a' href='mailto:support@athleteera.app' color='brand.solid' textDecoration='underline'>
                support@athleteera.app
              </Box>
            </Text>
          </Flex>
        </Stack>
      </Container>
    </Box>
  );
}

function Step({ number, title, body }: { number: string; title: string; body: string }) {
  return (
    <HStack align='start' gap={3}>
      <Box
        flexShrink={0}
        w={8}
        h={8}
        rounded='full'
        bg='brand.solid'
        color='brand.contrast'
        display='flex'
        alignItems='center'
        justifyContent='center'
        fontWeight='bold'
        fontSize='sm'
      >
        {number}
      </Box>
      <Stack gap={1}>
        <Text fontWeight='semibold'>{title}</Text>
        <Text color='fg.muted' fontSize='sm'>
          {body}
        </Text>
      </Stack>
    </HStack>
  );
}
